// A tiny inline prompt bar shared by the Source Control views: a title plus an
// InlineEditInput for entering a branch / tag name. Purely presentational and
// stateless toward git; the parent decides what the submitted name means. It
// owns only the draft text. Commit (Enter or blur) trims and forwards a
// non-empty name to `onSubmit`; an empty commit or Escape calls `onCancel`.

import { useState, type ReactElement } from 'react';
import { InlineEditInput } from '../components/InlineEditInput';

export interface GitNamePromptProps {
  /** Heading shown above the input (e.g. "New branch"). Also the input's a11y label. */
  title: string;
  /** Placeholder text for the empty input. */
  placeholder?: string;
  /** Seed value for the draft. */
  initial?: string;
  /** Called with the trimmed, non-empty name when the user commits. */
  onSubmit: (name: string) => void;
  /** Called when the user cancels (Escape, or committing an empty value). */
  onCancel: () => void;
}

export function GitNamePrompt({
  title,
  placeholder,
  initial = '',
  onSubmit,
  onCancel,
}: GitNamePromptProps): ReactElement {
  const [draft, setDraft] = useState(initial);

  // Enter / blur both route through InlineEditInput's onCommit. A non-empty
  // trimmed value submits; an empty one is treated as a cancel so we never
  // create a ref with a blank name.
  const commit = (): void => {
    const name = draft.trim();
    if (name) onSubmit(name);
    else onCancel();
  };

  return (
    <div className="mp-git-prompt">
      <span className="mp-git-prompt__title">{title}</span>
      <InlineEditInput
        value={draft}
        onChange={setDraft}
        onCommit={commit}
        onCancel={onCancel}
        ariaLabel={title}
        placeholder={placeholder}
        className="mp-git-prompt__input"
        selectOnMount="select"
      />
    </div>
  );
}
